import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { 
  X, 
  Minimize2, 
  Send, 
  ThumbsUp, 
  ThumbsDown,
  Sparkles,
  RotateCcw,
  History,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getCurrentCoachImageUrl } from "@/lib/coachImageHelper";
import { useTheme } from "@/context/ThemeContext";
import { useUserContext } from "@/context/UserContext";
import { useZillAi } from "@/context/ZillAiContext";
import type { Message, StructuredAction } from "@/context/ZillAiContext";
import { useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "@/firebase/useAuth";

function ChatBubble() {
  const { openChat } = useZillAi();
  const { profile } = useUserContext();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const backgroundColor = profile?.coachBackgroundColor || "#ffffff";

  return (
    <button
      onClick={() => openChat()}
      className="fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full shadow-2xl transition-all hover:scale-110 active:scale-95 hover:shadow-[0_20px_50px_rgba(0,0,0,0.3)]"
      style={{ 
        backgroundColor,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15)"
      }}
      aria-label="Open ZillAi chat"
    >
      {profile?.coachAvatarImage ? (
        profile.coachAvatarImage.startsWith('http') ? (
          <img src={getCurrentCoachImageUrl(profile.coachAvatarImage)} alt="Coach" className="h-full w-full object-contain rounded-full p-1" />
        ) : (
          <span className="text-5xl">{profile.coachAvatarImage}</span>
        )
      ) : (
        <Sparkles className="h-6 w-6 text-white" />
      )}
    </button>
  );
}

function MessageBubble({ 
  message, 
  onActionClick,
  onFeedback 
}: { 
  message: Message;
  onActionClick: (action: StructuredAction) => void;
  onFeedback: (messageId: string, helpful: boolean) => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isUser = message.role === "user";
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  const handleFeedback = (helpful: boolean) => {
    setFeedbackGiven(true);
    onFeedback(message.id, helpful);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "flex gap-2",
        isUser ? "justify-end" : "justify-start"
      )}
    >
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
          isUser
            ? isDark
              ? "bg-purple-600 text-white"
              : "bg-blue-500 text-white"
            : isDark
              ? "bg-slate-800 text-slate-100"
              : "bg-slate-100 text-slate-900"
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
        ) : (
          <div className="prose prose-sm max-w-none leading-relaxed dark:prose-invert prose-p:my-2 prose-ul:my-2 prose-li:my-0.5">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}
        
        {/* Quick Replies */}
        {message.quickReplies && message.quickReplies.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.quickReplies.map((reply, idx) => (
              <button
                key={idx}
                onClick={() => onActionClick({ label: reply.label, action: "GO_TO_LEARNING" as any, params: { reply: reply.value } })}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  isDark
                    ? "border-slate-600 bg-slate-700 hover:bg-slate-600"
                    : "border-slate-300 bg-white hover:bg-slate-50"
                )}
              >
                {reply.label}
              </button>
            ))}
          </div>
        )}

        {/* Structured Actions */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {message.suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => onActionClick(suggestion)}
                className={cn(
                  "rounded-lg border px-3 py-2 text-left text-xs font-medium transition-colors",
                  isDark
                    ? "border-purple-500/50 bg-purple-900/30 hover:bg-purple-900/50"
                    : "border-purple-300 bg-purple-50 hover:bg-purple-100"
                )}
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        )}

        {/* Feedback buttons for assistant messages */}
        {!isUser && (
          <div className="mt-2 flex items-center gap-2 pt-1">
            {!feedbackGiven ? (
              <>
                <button
                  onClick={() => handleFeedback(true)}
                  className={cn(
                    "rounded p-1 transition-colors",
                    isDark ? "hover:bg-slate-700" : "hover:bg-slate-200"
                  )}
                  aria-label="Helpful"
                >
                  <ThumbsUp className="h-3 w-3 opacity-60" />
                </button>
                <button
                  onClick={() => handleFeedback(false)}
                  className={cn(
                    "rounded p-1 transition-colors",
                    isDark ? "hover:bg-slate-700" : "hover:bg-slate-200"
                  )}
                  aria-label="Not helpful"
                >
                  <ThumbsDown className="h-3 w-3 opacity-60" />
                </button>
              </>
            ) : (
              <span className="text-xs opacity-60">Thanks for your feedback</span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function TypingIndicator({ isDark }: { isDark: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex gap-2 justify-start"
    >
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-4 py-3 text-sm",
          isDark ? "bg-slate-800 text-slate-100" : "bg-slate-100 text-slate-900"
        )}
      >
        <div className="flex gap-1.5 items-center">
          <motion.div
            className={cn("h-2.5 w-2.5 rounded-full", isDark ? "bg-slate-400" : "bg-slate-500")}
            animate={{ 
              y: [0, -8, 0]
            }}
            transition={{ 
              duration: 0.6, 
              repeat: Infinity, 
              repeatType: "loop",
              ease: "easeInOut"
            }}
          />
          <motion.div
            className={cn("h-2.5 w-2.5 rounded-full", isDark ? "bg-slate-400" : "bg-slate-500")}
            animate={{ 
              y: [0, -8, 0]
            }}
            transition={{ 
              duration: 0.6, 
              repeat: Infinity, 
              repeatType: "loop",
              ease: "easeInOut",
              delay: 0.15
            }}
          />
          <motion.div
            className={cn("h-2.5 w-2.5 rounded-full", isDark ? "bg-slate-400" : "bg-slate-500")}
            animate={{ 
              y: [0, -8, 0]
            }}
            transition={{ 
              duration: 0.6, 
              repeat: Infinity, 
              repeatType: "loop",
              ease: "easeInOut",
              delay: 0.3
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}

function ChatPanel() {
  const { messages, closeChat, minimizeChat, sendMessage, setQuickReply, clearMessages, conversationId, loadConversation } = useZillAi();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { profile } = useUserContext();
  const isDark = theme === "dark";
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [usage, setUsage] = useState<{
    limit: number;
    used: number;
    remaining: number;
    tier: string;
    bonusInteractions?: number;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (showHistory && user) {
      loadHistoryList();
    }
  }, [showHistory, user]);

  // Load usage stats on mount and after sending a message
  useEffect(() => {
    if (user) {
      loadUsageStats();
    }
  }, [user]); // Only reload when user changes, not when messages change

  const loadUsageStats = async () => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch("/api/zillai/usage", {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error("Failed to load usage stats:", error);
    }
  };

  const loadHistoryList = async () => {
    if (!user) return;
    
    setLoadingHistory(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch("/api/zillai/conversations", {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to load history");

      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error("Failed to load conversation history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;
    
    const message = inputValue.trim();
    setInputValue("");
    setIsSending(true);
    
    // Optimistically decrement the usage counter
    if (usage && usage.remaining > 0) {
      setUsage(prev => prev ? {
        ...prev,
        used: prev.used + 1,
        remaining: prev.remaining - 1
      } : null);
    }
    
    try {
      await sendMessage(message);
    } catch (error) {
      // Revert optimistic update on error
      if (usage && usage.remaining > 0) {
        setUsage(prev => prev ? {
          ...prev,
          used: prev.used - 1,
          remaining: prev.remaining + 1
        } : null);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleActionClick = (action: StructuredAction) => {
    // Handle quick replies
    if (action.params?.reply) {
      setQuickReply(action.params.reply);
      return;
    }

    // Handle navigation actions
    switch (action.action) {
      case "START_LESSON":
        if (action.params?.domainId && action.params?.lessonId) {
          navigate(`/lesson/${action.params.domainId}/${action.params.lessonId}`);
          minimizeChat();
        }
        break;
      case "OPEN_DOMAIN":
        if (action.params?.domainId) {
          navigate(`/learning/${action.params.domainId}`);
          minimizeChat();
        }
        break;
      case "GO_TO_JOURNAL":
        navigate("/journal");
        minimizeChat();
        break;
      case "GO_TO_RESILIENCE":
        navigate("/resilience-status");
        minimizeChat();
        break;
      case "GO_TO_LEARNING":
        navigate("/learning");
        minimizeChat();
        break;
      case "GO_TO_DAILY_CHALLENGE":
        navigate("/daily-challenge");
        minimizeChat();
        break;
      case "GO_TO_PROFILE":
        navigate("/profile");
        minimizeChat();
        break;
    }
  };

  const handleFeedback = async (messageId: string, helpful: boolean) => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      
      // Fetch CSRF token
      const csrfResponse = await fetch("/api/csrf-token", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!csrfResponse.ok) throw new Error("Unable to get security token");
      const { csrfToken } = await csrfResponse.json();
      
      await fetch("/api/zillai/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          messageId,
          helpful,
        }),
      });
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      className={cn(
        "fixed bottom-6 right-6 z-50 flex h-[600px] w-[400px] flex-col overflow-hidden rounded-2xl shadow-2xl",
        isDark ? "bg-slate-900 border border-slate-800" : "bg-white border border-slate-200"
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between border-b px-4 py-3",
          isDark
            ? "border-slate-800 bg-gradient-to-r from-purple-900/50 to-blue-900/50"
            : "border-slate-200 bg-gradient-to-r from-purple-100 to-blue-100"
        )}
      >
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            {profile?.coachAvatarImage ? (
              profile.coachAvatarImage.startsWith('http') ? (
                <img src={getCurrentCoachImageUrl(profile.coachAvatarImage)} alt="Coach" className="h-6 w-6 object-contain rounded-full" />
              ) : (
                <span className="text-xl">{profile.coachAvatarImage}</span>
              )
            ) : (
              <Sparkles className={cn("h-5 w-5", isDark ? "text-purple-400" : "text-purple-600")} />
            )}
            <span className="font-semibold">ZillAi</span>
          </div>
          {usage && (
            <div className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-600")}>
              {usage.remaining > 0 ? (
                <span>
                  {usage.remaining} message{usage.remaining !== 1 ? 's' : ''} remaining
                  {usage.bonusInteractions && usage.bonusInteractions > 0 ? ' 🎁' : ''}
                </span>
              ) : (
                <span className={isDark ? "text-red-400" : "text-red-600"}>
                  Limit reached - resets next month
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={cn(
              "rounded p-1.5 transition-colors",
              isDark ? "hover:bg-slate-800" : "hover:bg-slate-200",
              showHistory && (isDark ? "bg-slate-800" : "bg-slate-200")
            )}
            aria-label="Conversation History"
            title="View conversation history"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            onClick={clearMessages}
            className={cn(
              "rounded p-1.5 transition-colors",
              isDark ? "hover:bg-slate-800" : "hover:bg-slate-200"
            )}
            aria-label="New Conversation"
            title="Start new conversation"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            onClick={minimizeChat}
            className={cn(
              "rounded p-1.5 transition-colors",
              isDark ? "hover:bg-slate-800" : "hover:bg-slate-200"
            )}
            aria-label="Minimize"
            title="Minimize (continue conversation later)"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
          <button
            onClick={closeChat}
            className={cn(
              "rounded p-1.5 transition-colors",
              isDark ? "hover:bg-slate-800" : "hover:bg-slate-200"
            )}
            aria-label="Close"
            title="Close and save conversation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className={cn(
          "absolute inset-0 z-10 flex flex-col",
          isDark ? "bg-slate-900" : "bg-white"
        )}>
          <div className={cn(
            "flex items-center justify-between border-b px-4 py-3",
            isDark ? "border-slate-800" : "border-slate-200"
          )}>
            <h3 className="font-semibold">Conversation History</h3>
            <button
              onClick={() => setShowHistory(false)}
              className={cn(
                "rounded p-1.5 transition-colors",
                isDark ? "hover:bg-slate-800" : "hover:bg-slate-200"
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {loadingHistory && (
              <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
            )}
            
            {!loadingHistory && conversations.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No past conversations</p>
            )}
            
            {conversations.map((conv) => {
              const lastMessageDate = new Date(conv.lastMessageAt._seconds * 1000);
              const isCurrentConv = conversationId === conv.conversationId;
              
              return (
                <button
                  key={conv.conversationId}
                  onClick={() => {
                    const msgs: Message[] = conv.messages.map((m: any) => ({
                      id: m.id,
                      role: m.role,
                      content: m.content,
                      timestamp: m.createdAt._seconds * 1000,
                    }));
                    loadConversation(conv.conversationId, msgs);
                    setShowHistory(false);
                  }}
                  className={cn(
                    "w-full text-left p-3 rounded-lg transition-colors",
                    isDark 
                      ? "hover:bg-slate-800 border border-slate-800"
                      : "hover:bg-slate-50 border border-slate-200",
                    isCurrentConv && (isDark ? "bg-slate-800" : "bg-slate-100")
                  )}
                >
                  <div className="text-sm font-medium truncate">{conv.preview}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {conv.messages.length} messages • {lastMessageDate.toLocaleDateString()}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex h-full items-center justify-center text-center">
            <div className="space-y-2">
              {profile?.coachAvatarImage ? (
                profile.coachAvatarImage.startsWith('http') ? (
                  <img src={getCurrentCoachImageUrl(profile.coachAvatarImage)} alt="Coach" className="mx-auto h-20 w-20 object-contain rounded-lg" />
                ) : (
                  <span className="mx-auto block text-5xl">{profile.coachAvatarImage}</span>
                )
              ) : (
                <Sparkles className={cn("mx-auto h-12 w-12", isDark ? "text-slate-700" : "text-slate-300")} />
              )}
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                Hi! I'm ZillAi, your resilience coach.
                <br />
                How can I help you today?
              </p>
            </div>
          </div>
        )}
        
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onActionClick={handleActionClick}
            onFeedback={handleFeedback}
          />
        ))}
        
        {isSending && <TypingIndicator isDark={isDark} />}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        className={cn(
          "border-t p-3",
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        )}
      >
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className={cn(
              "min-h-[44px] max-h-[120px] resize-none text-sm",
              isDark 
                ? "bg-slate-800 border-slate-700 text-white placeholder:text-slate-400" 
                : "!bg-blue-50 !border-blue-200 text-slate-700 placeholder:text-slate-400"
            )}
            disabled={isSending}
          />
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || isSending}
            size="icon"
            className="h-11 w-11 flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function ZillAiWidget() {
  const { isOpen, isMinimized } = useZillAi();
  const location = useLocation();

  // Hide the coach widget entirely during the daily challenge to prevent cheating
  const HIDDEN_PATHS = ["/daily-crossword/play"];
  if (HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && !isMinimized && <ChatPanel key="chat-panel" />}
      </AnimatePresence>
      
      <AnimatePresence mode="wait">
        {!isOpen && <ChatBubble key="chat-bubble" />}
      </AnimatePresence>
    </>
  );
}
